import React, { useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import useToastAlert from "@/hooks/useToastAlert";
import useSubscriptions from "@/hooks/useSubscriptions";
import {
  PrimaryBlueButton,
  SecondaryBlueButton,
  Severity
} from "@velaro/velaro-component-lib";
import FormGroup from "@/components/FormGroup";
import { PageWidget } from "./index";
import WidgetEmbedCode from "./WidgetEmbedCode";
import DisplayRulesEditor, { DisplayRule } from "./DisplayRulesEditor";
import IconArrowBack2 from "@/icons/IconArrowBack2";

interface Props {
  widget: PageWidget;
  onClose(): void;
  onSaved(): void;
}

type Tab = "settings" | "display_rules" | "embed_code";

const TABS: { key: Tab; label: string }[] = [
  { key: "settings", label: "Settings" },
  { key: "display_rules", label: "Display Rules" },
  { key: "embed_code", label: "Embed Code" }
];

export default function EditWidgetPanel({ widget, onClose, onSaved }: Props) {
  const api = useApi();
  const { teams } = useTeams();
  const { subscription } = useSubscriptions();
  const toastAlert = useToastAlert();

  const [tab, setTab] = useState<Tab>("settings");
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState(widget.name);

  // Parse settings
  const parsedSettings = (() => {
    try { return JSON.parse(widget.settingsJson || "{}"); } catch { return {}; }
  })();
  const parsedRules: DisplayRule[] = (() => {
    try { return JSON.parse(widget.displayRulesJson || "[]"); } catch { return []; }
  })();

  const [phoneNumber, setPhoneNumber] = useState<string>(parsedSettings.phoneNumber ?? "");
  const [buttonText, setButtonText] = useState<string>(parsedSettings.buttonText ?? "");
  const [panelHeading, setPanelHeading] = useState<string>(parsedSettings.panelHeading ?? "");
  const [panelSubtitle, setPanelSubtitle] = useState<string>(parsedSettings.panelSubtitle ?? "");
  const [greeting, setGreeting] = useState<string>(parsedSettings.greeting ?? "Have questions? Just ask!");
  const [placeholder, setPlaceholder] = useState<string>(parsedSettings.placeholder ?? "Type your question here...");
  const [teamId, setTeamId] = useState<number>(parsedSettings.teamId ?? (teams[0]?.id ?? 0));
  const [primaryColor, setPrimaryColor] = useState<string>(parsedSettings.primaryColor ?? "#0066CC");
  const [position, setPosition] = useState<"bottom-right" | "bottom-left">(parsedSettings.position ?? "bottom-right");
  const [displayRules, setDisplayRules] = useState<DisplayRule[]>(parsedRules);
  // product_inquiry
  const [productNameSelector, setProductNameSelector] = useState<string>(parsedSettings.productNameSelector ?? "h1");

  function buildSettingsJson(): string {
    if (widget.widgetType === "click_to_call") {
      return JSON.stringify({ phoneNumber, buttonText, panelHeading, panelSubtitle, primaryColor, position });
    }
    if (widget.widgetType === "click_to_text") {
      return JSON.stringify({ phoneNumber, buttonText, panelHeading, panelSubtitle, primaryColor, position });
    }
    if (widget.widgetType === "product_inquiry") {
      return JSON.stringify({ productNameSelector, greeting, placeholder, buttonText, primaryColor, position, teamId });
    }
    return JSON.stringify({ greeting, placeholder, buttonText, primaryColor, position, teamId });
  }

  async function handleSave() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const res = await api.admin.put(`PageWidgets/${widget.id}`, {
        name: name.trim(),
        settingsJson: buildSettingsJson(),
        displayRulesJson: JSON.stringify(displayRules)
      });
      if (res.ok) {
        toastAlert.displayToast(Severity.Success, "Widget saved.");
        onSaved();
      } else {
        toastAlert.displayToast(Severity.Error, "Failed to save widget.");
      }
    } catch {
      toastAlert.displayToast(Severity.Error, "Failed to save widget.");
    } finally {
      setSaving(false);
    }
  }

  const widgetTypeLabel =
    widget.widgetType === "click_to_call" ? "Click to Call"
    : widget.widgetType === "click_to_text" ? "Click to Text"
    : widget.widgetType === "product_inquiry" ? "Product Inquiry"
    : "Topic Chat";

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Back + header */}
      <div className="flex items-center justify-between">
        <button
          onClick={onClose}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          <IconArrowBack2 />
          <span>Back to all widgets</span>
        </button>
        <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-1">{widgetTypeLabel}</span>
      </div>

      <div className="text-xl font-semibold">{widget.name}</div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 gap-4">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`pb-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t.key
                ? "border-cornflower-blue-500 text-cornflower-blue-600"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Settings tab */}
      {tab === "settings" && (
        <div className="flex flex-col gap-4 max-w-xl">
          <FormGroup label="Widget Name">
            <input
              className="input w-full"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </FormGroup>

          {(widget.widgetType === "click_to_call" || widget.widgetType === "click_to_text") && (
            <>
              <FormGroup label="Phone Number">
                <input
                  className="input w-full"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1-800-555-1234"
                />
              </FormGroup>
              <FormGroup label="Panel Heading">
                <input
                  className="input w-full"
                  value={panelHeading}
                  onChange={(e) => setPanelHeading(e.target.value)}
                  placeholder={widget.widgetType === "click_to_call" ? "Give us a call" : "Send us a text"}
                />
              </FormGroup>
              <FormGroup label="Subtitle" hint="Optional">
                <input
                  className="input w-full"
                  value={panelSubtitle}
                  onChange={(e) => setPanelSubtitle(e.target.value)}
                  placeholder="We're available Mon–Fri, 9am–5pm"
                />
              </FormGroup>
            </>
          )}

          {widget.widgetType === "topic_chat" && (
            <>
              <FormGroup label="Greeting Message">
                <input
                  className="input w-full"
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                />
              </FormGroup>
              <FormGroup label="Input Placeholder">
                <input
                  className="input w-full"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                />
              </FormGroup>
              <FormGroup label="Route to Team">
                <select
                  className="input w-full"
                  value={teamId}
                  onChange={(e) => setTeamId(Number(e.target.value))}
                >
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </FormGroup>
            </>
          )}

          {widget.widgetType === "product_inquiry" && (
            <>
              <div className="rounded-lg bg-cornflower-blue-50 border border-cornflower-blue-100 p-3 text-sm text-cornflower-blue-700">
                <i className="fa-solid fa-circle-info mr-2" />
                The widget reads the product name from the page using the CSS selector below and injects it into the conversation as a variable.
              </div>
              <FormGroup label="Product Name Selector" hint="CSS selector pointing to the product title on the page">
                <input
                  className="input w-full font-mono"
                  value={productNameSelector}
                  onChange={(e) => setProductNameSelector(e.target.value)}
                  placeholder="h1"
                />
              </FormGroup>
              <FormGroup label="Greeting" hint="Use {productName} to insert the detected product name">
                <input
                  className="input w-full"
                  value={greeting}
                  onChange={(e) => setGreeting(e.target.value)}
                  placeholder="Have a question about {productName}?"
                />
              </FormGroup>
              <FormGroup label="Input Placeholder">
                <input
                  className="input w-full"
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  placeholder="Ask your question..."
                />
              </FormGroup>
              <FormGroup label="Route to Team">
                <select
                  className="input w-full"
                  value={teamId}
                  onChange={(e) => setTeamId(Number(e.target.value))}
                >
                  {teams.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </FormGroup>
            </>
          )}

          <FormGroup label="Button Text" hint="Leave blank for default">
            <input
              className="input w-full"
              value={buttonText}
              onChange={(e) => setButtonText(e.target.value)}
              placeholder={
                widget.widgetType === "click_to_call" ? "📞 Call Us"
                : widget.widgetType === "click_to_text" ? "💬 Text Us"
                : "💬 Chat"
              }
            />
          </FormGroup>

          <FormGroup label="Brand Color">
            <div className="flex items-center gap-2">
              <input
                type="color"
                className="w-10 h-10 rounded cursor-pointer border border-gray-200"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
              <input
                className="input flex-1"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
            </div>
          </FormGroup>

          <FormGroup label="Position">
            <div className="flex gap-3">
              {(["bottom-right", "bottom-left"] as const).map((p) => (
                <button
                  key={p}
                  onClick={() => setPosition(p)}
                  className={`flex-1 py-2 rounded border text-sm transition-colors ${
                    position === p
                      ? "border-cornflower-blue-500 bg-cornflower-blue-50 text-cornflower-blue-700"
                      : "border-gray-200 hover:border-gray-300"
                  }`}
                >
                  {p === "bottom-right" ? "Bottom Right" : "Bottom Left"}
                </button>
              ))}
            </div>
          </FormGroup>

          <div className="flex gap-2 pt-2">
            <SecondaryBlueButton label="Cancel" onClick={onClose} />
            <PrimaryBlueButton
              label={saving ? "Saving..." : "Save"}
              onClick={handleSave}
              disabled={saving || !name.trim()}
            />
          </div>
        </div>
      )}

      {/* Display Rules tab */}
      {tab === "display_rules" && (
        <div className="flex flex-col gap-4 max-w-2xl">
          <div className="text-sm text-gray-500">
            Control which pages show this widget. Add URL conditions — the widget appears when ANY rule matches.
            Leave empty to show on all pages.
          </div>
          <DisplayRulesEditor rules={displayRules} onChange={setDisplayRules} />
          <div className="flex gap-2 pt-2">
            <SecondaryBlueButton label="Cancel" onClick={onClose} />
            <PrimaryBlueButton
              label={saving ? "Saving..." : "Save Rules"}
              onClick={handleSave}
              disabled={saving}
            />
          </div>
        </div>
      )}

      {/* Embed Code tab */}
      {tab === "embed_code" && (
        <WidgetEmbedCode
          widgetId={widget.widgetId}
          siteId={subscription?.siteId ?? 0}
        />
      )}
    </div>
  );
}
